import React, { useEffect, useMemo, useState } from "react";
import { Direction, Flexibility, IBundle, IRoute, IRouteLeg, IServiceInformation, IServiceType, getApiCallState, useStateSelector } from "../Models";
import { BookingFooter } from "../Components/BookingFooter";
import { getStrings } from "../../Shared/Reservation/StringResources";
import { useDispatch } from "react-redux";
import { clearTemporaryTable, createBooking, selectBundle, setStep } from "../Actions";
import dayjs from "dayjs";
import { Alert, Badge, Button, Col, Form, Modal, Row } from "react-bootstrap";
import { travelMethodClass } from "../Settings";
import SvgFastclock from "../../Shared/Icons/Fastclock";
import { formatDuration } from "../../Shared/Utils";
import { SpinnerSmall } from "../../Shared/Components/Spinner";
import { getErrorMessages } from "../Shared/Utils";
import { clearApiCallState } from "../ApiCalls/Actions";
import { isTest } from "../S3BookingApp";
import { Duration } from "dayjs/plugin/duration";
import { Price } from "../Components/Price";
import SvgAlertTriangle from "../../Shared/Icons/bootstrap/AlertTriangle";
import { Options, optionPrice } from "./Options";
import { getKrogenSlotForProductCode } from "./KrogenOptionsHelpers";

const strings = getStrings().component.selectJourney;

export const TravelDetails = (props: { direction: Direction }) => {

	const isTwoWay = useStateSelector(state => state.search.isTwoWay);
	const state = useStateSelector(state => state.travelDetails);
	const passengers = useStateSelector(state => state.timetable.searchJourneyResult?.offer?.passengers);
	const productFamilies = useStateSelector(state => state.timetable.searchJourneyResult?.offer?.productFamilies) || [];
	const apicalls = getApiCallState();
	const createBookingState = apicalls.getLoadingState("createBooking");

	//Interrail passes
	const interrailPasses = useStateSelector(state => state.search.interrail && state.search.interrailValidation?.isValid ? state.search.interrailValidation?.result?.map(x => x.number) : undefined);

	const travelState = state[props.direction];
	const route = travelState.route;
	const selectedBundle = travelState.bundle;

	const [flexibility, setFlexibility] = useState<string>();
	const [tablebookingValid, setTablebookingValid] = useState(true);
	const [showTablebookingWarning, setShowTablebookingWarning] = useState(false);

	const bookingErrors = useMemo(() => getErrorMessages("createBooking", apicalls), [createBookingState]);

	const footerPriceText = props.direction === "inbound" ? strings.returnJourney : (isTwoWay ? strings.outboundJourney : null);
	const routePrice = useMemo(() => {
		if (props.direction === "outbound")
			return state.outbound?.bundle?.price + optionPrice(state.outbound);
		else
			return (state.inbound?.bundle?.price || state.inbound?.route?.lowestPrice || 0) + optionPrice(state.inbound);
	}, [state.outbound?.bundle, state.inbound?.bundle, state.outbound?.options, state.inbound?.options, props.direction]);

	const flexibilities = useMemo(() => route?.bundles?.reduce((arr, curr) => {
		if (!arr.some(x => x == curr.flexibility))
			return arr.concat([curr.flexibility]);
		return arr;
	}, [] as Flexibility[]), [route?.bundles]);

	const availableBundles = useMemo(() => route?.bundles?.filter(x => x.requiredItems.length > 0).filter(x => x.flexibility == flexibility) || [], [route, flexibility]);

	//Skillnaden till att byta till annan flexibilitet, kan bli null om motsvarande produktfamilj saknas
	const flexdiff = useMemo(() => flexibilities?.map(flex => ({ flex, diff: route?.bundles.find(x => x.flexibility === flex && x.productFamilyId === selectedBundle?.productFamilyId)?.price - selectedBundle?.price })), [route?.bundles, selectedBundle]);

	const showFlexWarning = props.direction === "inbound" && state.outbound?.bundle && selectedBundle && state.outbound.bundle.flexibility !== selectedBundle.flexibility;

	const getBundleName = (bundle: IBundle) => {
		return productFamilies.find(x => x.id === bundle.productFamilyId)?.name || bundle.productFamilyId;
	};

	const submit = () => {
		//TODO: IsValid
		if (!tablebookingValid)
		{
			setShowTablebookingWarning(true);
			return;
		}
		setShowTablebookingWarning(false);

		if (isTwoWay && props.direction === "outbound")
			dispatch(setStep("inbound"));
		else
			dispatch(createBooking(state, passengers, interrailPasses));
	};

	const back = () => {

		//Avboka alla temporära bordsbokningar!
		const tableBookings = state.tableBookings?.bookings?.filter(x => route?.legs?.some(leg => leg.id === x.legId)) || [];
		tableBookings.forEach(x => dispatch(clearTemporaryTable(x.bookingId)));


		if (props.direction === "outbound")
			dispatch(setStep("timetable"));
		else
			dispatch(setStep("outbound"));
	};

	const errorModalClosed = () => {
		dispatch(clearApiCallState("createBooking"));
	}

	//Välj billigaste bundlen till att börja med
	useEffect(() => {
		//Om vi är på utresan redan har en bundle på hemresan så har vi just backat och behöver inte ändra något
		if (props.direction === "outbound" && state.outbound?.bundle && state.inbound?.bundle)
		{
			setFlexibility(state.outbound.bundle.flexibility);
			return;
		}

		if (route?.bundles?.length > 0)
		{
			//Om det är hemresa vill vi välja samma flex som på utresan
			const flex = props.direction === "inbound" ? state.outbound?.bundle?.flexibility : null;

			const cheapestBundle = getCheapestBundle(route.bundles, flex);
			dispatch(selectBundle(props.direction, cheapestBundle));
			setFlexibility(cheapestBundle.flexibility);
		}
	}, [route?.bundles]);

	//Om vi byter flexibilitet måste vi byta bundle
	useEffect(() => {
		if (!flexibility || !route?.bundles || flexibility === selectedBundle?.flexibility)
			return;

		let bundles = route.bundles.filter(x => x.flexibility === flexibility);

		//Finns bundle med samma productfamilyid?
		let newBundle = bundles.find(x => x.productFamilyId === selectedBundle.productFamilyId);

		//Annars, försök hitta en med samma comfortclass och travelclass
		if (!newBundle)
		{
			const currentProductFamily = productFamilies.find(x => x.id === selectedBundle.productFamilyId);
			const matchingBundles = bundles.filter(x => {
				var productFamily = productFamilies.find(pf => pf.id === x.productFamilyId);
				return currentProductFamily.travelClass === productFamily.travelClass && currentProductFamily.comfortClass === productFamily.comfortClass;
			});

			//Om vi hittade några som matchar väljer vi i första hand den som heter likadant, i andra hand den billigaste
			if (matchingBundles.length > 0)
			{
				var currentBundleName = getBundleName(selectedBundle);
				newBundle = matchingBundles.find(x => getBundleName(x) === currentBundleName) || getCheapestBundle(matchingBundles);
			}
		}

		//Annars ta billigaste med den valda flexibiliteten
		if (!newBundle)
			newBundle = getCheapestBundle(route.bundles, flexibility);
		dispatch(selectBundle(props.direction, newBundle));
	}, [flexibility]);

	//Kontrollera att vi har bordsbokningar för alla tillval som kräver det
	useEffect(() => {
		setShowTablebookingWarning(false);

		if (!travelState.options)
			return;

		let valid = true;
		const legs = Object.getOwnPropertyNames(travelState.options);
		legs.forEach(legId => {
			const options = travelState.options[legId];
			if (!options)
				return;

			const productCodes = Object.getOwnPropertyNames(options);
			productCodes.forEach(productCode => {
				const slot = getKrogenSlotForProductCode(productCode);
				if (!slot)
					return;

				const hasTableBooking = state.tableBookings?.bookings?.some(x => x.slotName === slot.slotName) ? true : false;
				valid = valid && hasTableBooking;
			});
		});

		// console.debug("Options valid", valid);
		setTablebookingValid(valid);

	}, [travelState.options, state.tableBookings?.bookings]);


	const dispatch = useDispatch();


	return <>
		<div className="body">
			<div className="select-journey content">
				<Row>
					<Col lg={6}>
						<JourneyHeading direction={props.direction} isTwoWay={isTwoWay} route={route} />
						<RouteMap route={route} />
					</Col>
					<Col lg={6}>
						<section>
							<h2>{strings.journeyAdvice.priceGroupHeading}</h2>
							<Form.Group controlId="selectBundle">
								<Form.Label>{strings.journeyAdvice.priceGroupLabel}</Form.Label>
								{ availableBundles.map(b =>
									<Form.Check key={b.id} custom type="radio" id={`bundle_${b.id}`}
										checked={b.id === selectedBundle?.id}
										onChange={() => dispatch(selectBundle(props.direction, b))}
										label={<>
											{getBundleName(b)} <PriceDiff diff={b.price - selectedBundle?.price} />
											{isTest() && <Badge variant="light" className="ml-4">{b.productFamilyId}, {b.price} kr</Badge>}
										</>}
									/>)
								}
							</Form.Group>

							{ flexibilities?.length > 1 && props.direction === "outbound" && <Form.Group>
								<Form.Label>{strings.journeyAdvice.flexibilityLabel}</Form.Label>
								{ flexibilities.map(f =>
									<Form.Check key={f} custom type="radio" id={`flex_${f}`} name="flexibility"
										checked={f === flexibility} onChange={() => setFlexibility(f)}
										label={<>
											{getFlexLabel(f)} <PriceDiff diff={flexdiff?.find(x => x.flex === f)?.diff} />
											{ showFlexWarning && f === flexibility && <Badge variant="warning" className="ml-3"><SvgAlertTriangle /> {strings.journeyAdvice.preferredFlexWarning}</Badge> }
										</>} />
									)
								}
							</Form.Group>}
							{ (props.direction === "inbound" || flexibilities?.length === 1) &&
								<p>{strings.journeyAdvice.flexibilityLabel} <strong>{getFlexLabel(flexibility)?.toLocaleLowerCase()}</strong></p>}
						</section>

						<Options direction={props.direction} travelDetails={state} />

						<Alert show={showTablebookingWarning} variant="danger">Bordsbokning måste göras för alla förbeställda måltider i Krogen</Alert>
					</Col>
				</Row>


				{/* <pre className="mt-5"><code>{JSON.stringify(route, null, 2)}</code></pre> */}

			</div>


			<BookingFooter text={strings.journeyAdvice.detailsSubmit} onSubmit={submit} backText={strings.timetableBack} onBack={back} priceText={footerPriceText} price={routePrice} />
		</div>

		<Modal show={createBookingState === "loading"} backdrop="static" keyboard={false} centered>
			<Modal.Body>
				<div className="mt-3 mb-3">{strings.creatingOrder} <SpinnerSmall /></div>
			</Modal.Body>
		</Modal>
		<Modal show={createBookingState === "error"} backdrop="static" centered onHide={errorModalClosed}>
			<Modal.Header><Modal.Title>{strings.errorCreatingOrder}</Modal.Title></Modal.Header>
			<Modal.Body>
				{strings.errorCreatingOrderDetails}<br />
				{bookingErrors?.length > 0 ?
					bookingErrors?.map(e => <>{e}<br /></>) : "Unknown error"
				}
			</Modal.Body>
			<Modal.Footer>
				<Button variant="secondary" onClick={errorModalClosed}>OK</Button>
			</Modal.Footer>
		</Modal>
	</>;
}

function getFlexLabel(flex: string) {
	switch(flex) {
		case "nonrefundable": return strings.journeyAdvice.flexibilityFIX;
		case "rebookable": return strings.journeyAdvice.flexibilityFLEX;
		case "refundable": return strings.journeyAdvice.flexibilityFULL;
		default: return flex;
	}
}

const getCheapestBundle = (bundles: IBundle[], flex?: string) => {

	if (flex && bundles.some(x => x.flexibility === flex))
		bundles = bundles.filter(x => x.flexibility === flex);

	return bundles.reduce((cheapest, b) => !cheapest || b.price < cheapest.price ? b : cheapest, null);
}

export const PriceDiff = (props: { diff: number }) => {
	if (props.diff > 0)
		return <span className="price-diff positive">+<Price amount={props.diff} /></span>;
	if (props.diff < 0)
	return <span className="price-diff negative"><Price amount={props.diff} /></span>;

	return null;
}


export const JourneyHeading = (props: { direction: Direction, route: IRoute, isTwoWay: boolean }) => {

	if (!props.route.legs || props.route.legs.length === 0)
		return null;

	const origin = props.route.legs[0].departureStation;
	const destination = props.route.legs[props.route.legs.length-1].arrivalStation;
	const departure = dayjs(origin.departureTimestamp);

	return <section>
		<h2>{props.isTwoWay ?(props.direction === "outbound" ? strings.outboundJourney : strings.returnJourney) : strings.singleJourney}</h2>
		<h3>{origin.name} ➔ {destination.name}</h3>
		<p className="date">{departure.format("dddd LL")}</p>
	</section>;
};

//TODO: Detta måste testat med en resa som faktiskt har byten!
export const RouteMap = (props: { route: IRoute }) => {
	if (!props.route.legs || props.route.legs.length === 0)
		return null;

	const segments = useMemo(() => {
		const legs = [...props.route.legs];

		//Stoppa in byten mellan varje segment
		for(let i=0; i<legs.length-1; i++) {
			const arrivalStation = legs[i].arrivalStation;
			const departureStation = legs[i+1].departureStation;

			const changeLeg = {
				serviceName: "0",
				serviceType: { code: "", name: "Byte", modality: "" },
				departureStation: {
					departureTimestamp: arrivalStation.arrivalTimestamp, name: "", uicStationCode: ""
				},
				arrivalStation: {
					arrivalTimestamp: departureStation.departureTimestamp, name: "", uicStationCode: ""
				}
			} as IRouteLeg;

			if (arrivalStation.uicStationCode !== departureStation.uicStationCode)
				changeLeg.serviceType.modality = "G";

			legs.splice(i+1, 0, changeLeg);
			i=i+1;
		}

		//Skapa segment med förberäknade durations etc
		return legs.map(leg => {
			const departure = dayjs(leg.departureStation.departureTimestamp);
			const arrival = dayjs(leg.arrivalStation.arrivalTimestamp);
			const duration = dayjs.duration(arrival.diff(departure));

			return {
				serviceName: leg.serviceName,
				serviceType: leg.serviceType,
				from: leg.departureStation.name,
				to: leg.arrivalStation.name,
				serviceInformation: leg.serviceInformation,
				departure,
				arrival,
				duration,
			} as IRouteSegment;
		});

	}, [props.route]);

	return <div className="segment-map">
		{segments.map((s, idx) => s.serviceName === "0" ? <ChangeSegment segment={s} key={idx} /> : <RouteSegment segment={s} key={idx} />)}
	</div>
};

interface IRouteSegment
{
	serviceName: string,
	serviceType: IServiceType,
	from: string,
	to: string,
	serviceInformation?: IServiceInformation,
	departure: dayjs.Dayjs,
	arrival: dayjs.Dayjs,
	duration: Duration,
}

const RouteSegment = (props: { segment: IRouteSegment }) => {
	const s = props.segment;
	return <div className="segment">
		<h3 className="segment-heading">{s.departure.format("H:mm")} {s.from}</h3>
		{/* <h3 className="segment-heading d-md-none d-lg-none d-xl-none">{s.departureDateTime.format("H:mm")} {s.departureLocation.locationNameShort}</h3> */}
		<div className={`segment-data ${travelMethodClass(s.serviceType.modality)}`}>
			<Producer {...s} /><br />
			{s.serviceType.name} {s.serviceName}<br />
			{strings.journeyAdvice.duration} <SvgFastclock /> {formatDuration(s.duration)}<br />
			{s.serviceInformation && <>
				{s.serviceInformation.serviceProperties?.length > 0 && <ul className="list-inline">
					{s.serviceInformation.serviceProperties.map(x => <li key={x.code} className="list-inline-item">
					<span className={`travelicon-${x.code}`} title={x.description} aria-label={x.description}></span>
					</li>)}
				</ul>}
				{s.serviceInformation.travelInfo?.filter(x => x.content).map((info, idx) => <p key={idx} className="segment-info" dangerouslySetInnerHTML={{ __html: info.content }}></p>)}
			</>}
		</div>
		<h3 className="segment-footer">{s.arrival.format("H:mm")} {s.to}</h3>
		{/* <h3 className="segment-footer d-md-none d-lg-none d-xl-none">{s.arrivalDateTime.format("H:mm")} {s.arrivalLocation.locationName}</h3> */}
	</div>;
}

const ChangeSegment =  (props: { segment: IRouteSegment }) => {

	return <div className="segment segment-change">
		<div className={`segment-data ${travelMethodClass(props.segment.serviceType.modality)}`}>
			{strings.journeyAdvice.change} <SvgFastclock /> {formatDuration(props.segment.duration)}
		</div>
	</div>
}

export const Producer = (props: { serviceType: IServiceType }) => {
	if (props.serviceType.code?.startsWith("ST") || props.serviceType.name?.startsWith("Snälltåget"))
		return <img src="/img/Snalltaget_logo.svg" className="producer" alt="Snälltåget" />;

	//TODO: Detta måste kollas hur det blir om vi har andra producenter än Snälltåget!
	return null; //<span className="producer">{props.serviceType.name}</span>
};